import { Injectable } from "@angular/core";
import { BottomSheetHelperService } from "./bottomSheetHelper.service";
import { orderTypes, Holiday } from "../shared/models";
import * as moment from 'moment';
import Utils from '../shared/utils';
import { WarningdialogComponent } from "../modalDialogs/warningDialog/warningDialog.component";

// Daily event modes for filtering calculation
type DailyMode = 'Always' | 'JustAtSpecialDays' | 'NotAtSpecialDays'
type MonthlyMode = 'ByDate' | 'ByWeekday';
type YearlyMode = 'ByDate' | 'ByWeekday';

export interface IEvent {
  id: number;
  title: string;
  locations: number[];
  pricelevel: number;
  webshopOrdertypes: orderTypes[];
  sotOrdertypes: orderTypes[];
  sortNr: number;
  start_date_ymd: number;
  end_date_ymd: number;
  start_time_hms: number;
  end_time_hms: number;
  recurrence: RecurrenceAndOptions
}

type RecurrenceAndOptions =
  {
    tag: "daily",
    data: DailyOptions,
  }
  |
  {
    tag: "weekly",
    data: WeeklyOptions,
  }
  |
  {
    tag: "monthly",
    data: MonthlyOptions,
  }
  |
  {
    tag: "yearly",
    data: YearlyOptions,
  }

// Options for daily recurring events
interface DailyOptions {
  interval: number; // Every N days (e.g., every 3 days)
  mode: DailyMode; // When to calculate: always, weekends only, or weekdays only
}

// Options for weekly recurring events
interface WeeklyOptions {
  intervalWeeks: number; // Every N weeks within date range
  daysOfWeek: number[]; // ISO weekdays [1=Monday ... 7=Sunday]
  mode: DailyMode; // Filter by weekend/weekday logic
}

// Options for monthly recurring events
interface MonthlyOptions {
  mode: MonthlyMode; // By specific date or by weekday pattern
  intervalMonths: number; // Every N months
  dayOfMonth?: number; // For ByDate mode: which day of month (1-31)
  weekNumber?: number; // For ByWeekday mode: which week (1-4: erste, zweite, dritte, vierte)
  weekday?: number; // For ByWeekday mode: which weekday (1-7)
}

// Options for yearly recurring events
interface YearlyOptions {
  mode: YearlyMode; // By specific date or by weekday pattern
  month: number; // Which month (1-12)
  day?: number; // For ByDate mode: which day of month
  weekNumber?: number; // For ByWeekday mode: which week of month
  weekday?: number; // For ByWeekday mode: which weekday
}

@Injectable({
  providedIn: "root",
})
export class EventmanagerService {
  constructor(
    private _bottomSheetHelperService: BottomSheetHelperService,
  ) { }

  public OpenPriceChangeDialog(groupOrder: boolean = false) {
    const me = this;
    me._bottomSheetHelperService.open(WarningdialogComponent, {
      data: {
        title: "phExpiredEvent_Title"
        , content: groupOrder
          ? "phExpiredEvent_Description_GroupOrder"
          : "phExpiredEvent_Description_Single"
        , btnLabel: "phGlobal_Continue"
      },
      panelClass: "mat-bottom-sheet-container-medium"
    });
  }

  /**
   * Returns the event for the current time and for the given location and ordertype.
   * @returns 
   */
  public filterActiveEventNow(events: IEvent[], holidays: Holiday[]): IEvent {
    const me = this;
    return me.filterActiveEventForMoment(events, moment(), holidays);
  }

  /**
   * Returns the event for the given datetime, location, and order type.
   * @returns The event that matches the criteria, or null if not found.
   */
  public filterActiveEventForMoment(events: IEvent[], datetime: moment.Moment, holidays: Holiday[]): IEvent {
    const me = this;
    let activeEvent: IEvent | null = null;
    let lowestSortNr = Number.MAX_SAFE_INTEGER;

    for (const event of events) {
      //check holidays
      if (event.recurrence.data.mode === 'JustAtSpecialDays' && !me.isHoliday(datetime, holidays)) {
        continue; // Skip if only on holidays but today is not a holiday
      }
      else if (event.recurrence.data.mode === 'NotAtSpecialDays' && me.isHoliday(datetime, holidays)) {
        continue; // Skip if not on holidays but today is a holiday
      }

      // Check if the event is valid for the specific datetime considering recurrence
      if (me.isEventValidForDateTime(event, datetime)) {
        // Check if this event has a lower sortNr than the current best
        if (event.sortNr < lowestSortNr) {
          activeEvent = event;
          lowestSortNr = event.sortNr;
        }
      }
    }
    return activeEvent; // Return the valid event with the lowest sortNr
  }

  /**
   * Checks if the given date is a holiday. Also see getTimeSetDate in product.model.ts
   * @param date The date to check.
   * @param holidays The list of holidays.
   * @returns True if the date is a holiday, false otherwise.
   */
  private isHoliday(date: moment.Moment, holidays: Holiday[]): boolean {
    if (!holidays || holidays.length === 0) {
      return false;
    }
    const date_ymd = parseInt(date.format('YYYYMMDD'));
    const date_hms = parseInt(date.format('HHmmss'));

    const find_holiday = (day_ymd: number, day_hms: number): ((day: Holiday) => boolean) => {
      return (day: Holiday): boolean => {
        // ist Feiertag im Zeitraum
        return (day_ymd > day.begin_date_ymd && day_ymd < day.end_date_ymd)
          || // oder ist am ersten Tag und in der Zeitspanne
          (day_ymd == day.begin_date_ymd && day_hms > day.begin_time_hms && (day_hms < day.end_time_hms || day_ymd < day.end_date_ymd))
          || // oder ist am letzten Tag und in der Zeitspanne
          (date_ymd == day.end_date_ymd && day_hms < day.end_time_hms && (day_hms > day.begin_time_hms || day_ymd > day.begin_date_ymd));
      }
    }
    const holiday = holidays.find(find_holiday(date_ymd, date_hms));
    if (holiday) {
      return true;
    } // else heute kein Feiertag
    return false;
  }

  //Filter for Ordertype and Location
  public filterEventsByOrderTypeAndLocation(events: IEvent[], orderType: number, locationId: number, sotEvents: boolean): IEvent[] {
    let filteredEvents: IEvent[] = [];
    if (sotEvents) {
      filteredEvents = events.filter(event =>
        event.sotOrdertypes && event.sotOrdertypes.includes(orderType) &&
        event.locations && event.locations.includes(locationId)
      );
    }
    else {
      filteredEvents = events.filter(event =>
        event.webshopOrdertypes && event.webshopOrdertypes.includes(orderType) &&
        event.locations && event.locations.includes(locationId)
      );
    }
    return filteredEvents;
  }

  /**
   * Checks if an event is valid for a specific datetime considering recurrence rules and time constraints
   * @param event The event to check
   * @param datetime The datetime to check against
   * @returns True if the event is valid for the given datetime
   */
  private isEventValidForDateTime(event: IEvent, datetime: moment.Moment): boolean {
    const me = this;

    // Check if event end date has passed
    const eventEndDateTime = Utils.GetMomentFromInt(event.end_date_ymd, event.end_time_hms);
    if (datetime.isAfter(eventEndDateTime)) {
      return false; // Event has ended
    }

    // Check if event has not started yet
    const eventStartDateTime = Utils.GetMomentFromInt(event.start_date_ymd, event.start_time_hms);
    if (datetime.isBefore(eventStartDateTime)) {
      return false; // Event has not started yet
    }

    // First check if the time of day matches
    const eventStartTime = Utils.GetMomentFromInt(0, event.start_time_hms);
    const eventEndTime = Utils.GetMomentFromInt(0, event.end_time_hms);
    const dateTimeFormated = datetime.format('HH:mm:ss');
    const isInTimeRange = (dateTimeFormated >= eventStartTime.format('HH:mm:ss') && dateTimeFormated <= eventEndTime.format('HH:mm:ss'));
    if (!isInTimeRange) {
      return false; //liegt außerhalb der Zeit
    }

    // Check recurrence rules
    switch (event.recurrence.tag) {
      case 'daily':
        return me.isDailyRecurrenceValid(event, datetime);
      case 'weekly':
        return me.isWeeklyRecurrenceValid(event, datetime);
      case 'monthly':
        return me.isMonthlyRecurrenceValid(event, datetime);
      case 'yearly':
        return me.isYearlyRecurrenceValid(event, datetime);
      default:
        return false;
    }
  }

  /**
   * Validates daily recurrence rules
   */
  private isDailyRecurrenceValid(event: IEvent, datetime: moment.Moment): boolean {
    const options = event.recurrence.data as DailyOptions;
    const eventStart = Utils.GetMomentFromInt(event.start_date_ymd, event.start_time_hms);

    // Calculate days difference
    const daysDiff = datetime.diff(eventStart, 'days');

    // Check if it matches the interval
    if (daysDiff % options.interval !== 0) {
      return false;
    }

    return true;
  }

  /**
   * Validates weekly recurrence rules
   */
  private isWeeklyRecurrenceValid(event: IEvent, datetime: moment.Moment): boolean {
    const options = event.recurrence.data as WeeklyOptions;
    const eventStart = Utils.GetMomentFromInt(event.start_date_ymd, event.start_time_hms);

    // Check if the weekday is in the allowed days
    const weekday = datetime.isoWeekday(); // ISO weekdays [1=Monday ... 7=Sunday]
    if (!options.daysOfWeek.includes(weekday)) {
      return false;
    }

    // Calculate weeks difference
    const weeksDiff = datetime.diff(eventStart, 'weeks');

    // Check if it matches the interval
    if (weeksDiff % options.intervalWeeks !== 0) {
      return false;
    }

    return true;
  }

  /**
   * Validates monthly recurrence rules
   */
  private isMonthlyRecurrenceValid(event: IEvent, datetime: moment.Moment): boolean {
    const options = event.recurrence.data as MonthlyOptions;
    const eventStart = Utils.GetMomentFromInt(event.start_date_ymd, event.start_time_hms);

    // Calculate months difference
    const monthsDiff = datetime.diff(eventStart, 'months');

    // Check if it matches the interval
    if (monthsDiff % options.intervalMonths !== 0) {
      return false;
    }

    if (options.mode === 'ByDate') {
      // Check specific day of month
      return datetime.date() === options.dayOfMonth;
    } else if (options.mode === 'ByWeekday') {
      // Check weekday pattern (e.g., "first Monday of the month")
      const weekday = datetime.isoWeekday();
      if (weekday !== options.weekday) {
        return false;
      }

      // Calculate which week of the month this is
      const weekOfMonth = Math.ceil(datetime.date() / 7);

      return weekOfMonth === options.weekNumber;
    }

    return false;
  }

  /**
   * Validates yearly recurrence rules
   */
  private isYearlyRecurrenceValid(event: IEvent, datetime: moment.Moment): boolean {
    const options = event.recurrence.data as YearlyOptions;
    const eventStart = Utils.GetMomentFromInt(event.start_date_ymd, event.start_time_hms);

    // Check if it's the correct month
    if (datetime.month() + 1 !== options.month) {
      return false;
    }

    // Calculate years difference
    const yearsDiff = datetime.year() - eventStart.year();

    // For yearly events, we assume interval of 1 year
    if (yearsDiff < 0) {
      return false;
    }

    if (options.mode === 'ByDate') {
      // Check specific day of month
      return datetime.date() === options.day;
    } else if (options.mode === 'ByWeekday') {
      // Check weekday pattern (e.g., "first Monday of the month")
      const weekday = datetime.isoWeekday();
      if (weekday !== options.weekday) {
        return false;
      }

      // Calculate which week of the month this is
      const weekOfMonth = Math.ceil(datetime.date() / 7);

      return weekOfMonth === options.weekNumber;
    }

    return false;
  }
}